import {
  Box,
  Checkbox,
  Flex,
  IconButton,
  useDisclosure,
  useToast,
  useToken,
} from "@chakra-ui/react";
import { capitalizeEveryWord } from "@equidefi/shared/helpers/string";
import { useFormik } from "formik";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Select from "react-select";
import * as Yup from "yup";

import { SignerModal } from "@equidefi/portals/components/SignerModal";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useInvestmentSigners } from "@equidefi/portals/hooks/useSigners";
import { useSaveVaultEntity } from "@equidefi/portals/hooks/useVault";
import { userFullName } from "@equidefi/shared";
import {
  ENTITY_SUB_TYPE_OPTIONS,
  JOINT_SUB_TYPE_OPTIONS,
  TRUST_SUB_TYPE_OPTIONS,
} from "@equidefi/shared/constants/profile";
import { CardList, CardListItem, Heading, Password, Text } from "@equidefi/ui";
import { Input } from "@equidefi/ui/forms";
import { DateField } from "@equidefi/ui/forms/DateField";
import { Icon } from "@equidefi/ui/icon";

import WorkflowProtection from "@/components/workflow/WorkflowProtection";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { MESSAGE } from "../../../../../issuer/src/constants/forms";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";

export const entityTypeSchema = Yup.object().shape({
  company: Yup.string()
    .nullable()
    .test("company-required-for-entity-or-trust", function (value) {
      const { ta_account_type } = this.parent;
      if (
        (ta_account_type === "ENTITY" || ta_account_type === "TRUST") &&
        !value?.trim()
      ) {
        return this.createError({
          message:
            ta_account_type === "TRUST"
              ? MESSAGE.REQUIRED("Trust Name")
              : MESSAGE.REQUIRED("Entity Name"),
        });
      }
      return true;
    }),
  tax_id: Yup.string()
    .nullable()
    .test("ssn-required", function (value) {
      if (!value?.trim()) {
        return this.createError({
          message: MESSAGE.REQUIRED("Social Security or Tax ID"),
        });
      }
      if (!/^\d{9}$/.test(value.trim())) {
        return this.createError({
          message: "Must be exactly 9 digits, numbers only (no dashes)",
        });
      }
      return true;
    }),
  birthdate: Yup.date()
    .nullable()
    .test("birthdate-required", function (value) {
      if (!value) {
        return this.createError({
          message:
            "Please include your birthdate, and you must be at least 18 years old.",
        });
      }
      const cutoff = moment().subtract(18, "years").add(1, "day").toDate();
      if (value > cutoff) {
        return this.createError({
          message: "You must be at least 18 years old.",
        });
      }

      return true;
    }),
  ein: Yup.string()
    .nullable()
    .test("ein-required-for-entity-or-trust", function (value) {
      const { ta_account_type } = this.parent;
      if (ta_account_type === "ENTITY" || ta_account_type === "TRUST") {
        if (!value?.trim()) {
          return this.createError({
            message: MESSAGE.REQUIRED("EIN"),
          });
        }
        if (!/^\d{9}$/.test(value.trim())) {
          return this.createError({
            message: "Must be exactly 9 digits, numbers only (no dashes)",
          });
        }
      }
      return true;
    }),
  ta_sub_type: Yup.string()
    .nullable()
    .test("individual-sub-type-required-if-joint", function (value) {
      const { ta_account_type } = this.parent;
      if (
        (ta_account_type === "ENTITY" ||
          ta_account_type === "TRUST" ||
          ta_account_type === "JOINT") &&
        !value?.trim()
      ) {
        return this.createError({
          message: MESSAGE.REQUIRED("Joint Type"),
        });
      }
      return true;
    }),
});

export const accountTypeHelperText = {
  INDIVIDUAL: {
    text: "Shares will be issued to you only.",
    header: "Individual",
  },
  ENTITY: {
    text: "Shares will be issued to an entity, such as a Company, LLC, etc...",
    header: "Entity",
  },
  JOINT: {
    text: "Shares will be issued to you and another person.",
    header: "Trust",
  },
  TRUST: {
    text: "Shares will be issued to a trust.",
    header: "Trust",
  },
};

export const WorkflowSigners = () => {
  const { investment, offering, user, refetchInvestment } =
    useWorkflowContext();
  useWorkflowPosition(5);
  const [equidefiRed, equidefiBlue] = useToken("colors", [
    "equidefi.red",
    "equidefi.blue",
  ]);
  const toast = useToast();
  const location = useLocation();
  const [spinner, showSpinner] = useState(false);
  const navigate = useNavigate();
  const [disableEIN, setDisableEIN] = useState(false);
  useDocumentTitle(["Signers", offering?.name, investment?.issuer?.name]);

  const entityUpdate = useSaveVaultEntity(investment?.id);
  const modal = useDisclosure();

  const onSubmit = async (data) => {
    showSpinner(true);
    let page = "kyc";

    if (location.state?.returnTo) {
      page = location.state.returnTo;
    }

    const payload = {
      company: data.company,
      ta_sub_type: data.ta_sub_type?.value,
      tax_id: data.tax_id,
      birthdate: data.birthdate,
      ein: data.ein,
      skip_transition: location.state?.skip_transition,
    };

    if (
      investment.vault?.ta_account_type === "INDIVIDUAL" ||
      investment.vault?.ta_account_type === "JOINT"
    ) {
      payload.company = undefined;
      payload.ein = undefined;
    }

    try {
      await entityUpdate.mutateAsync(payload);

      await refetchInvestment();

      navigate(`/offerings/${offering.slug}/${investment?.id}/${page}`, {
        state: {
          refresh: !!location.state?.returnTo,
        },
      });
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description:
          error.response?.data?.errors?.join(". ") ?? "Something went wrong",
      });
    } finally {
      showSpinner(false);
    }
  };

  const subTypeOptions = useMemo(() => {
    switch (investment?.vault?.ta_account_type) {
      case "ENTITY":
        return ENTITY_SUB_TYPE_OPTIONS;
      case "TRUST":
        return TRUST_SUB_TYPE_OPTIONS;
      case "JOINT":
        return JOINT_SUB_TYPE_OPTIONS;
      default:
        return [];
    }
  }, [investment?.vault?.ta_account_type]);

  const {
    handleSubmit,
    setFieldValue,
    getFieldProps,
    values,
    errors,
    touched,
    isValid,
    setTouched,
  } = useFormik({
    initialValues: {
      company: investment.vault.company,
      ta_sub_type: subTypeOptions.find(
        (option) => option.value === investment.vault.ta_sub_type,
      ),
      ein: investment.vault.ein,
      tax_id: investment.vault.tax_id,
      birthdate: investment.vault.birthdate,
      ta_account_type: investment.vault.ta_account_type,
    },
    enableReinitialize: true,
    validationSchema: entityTypeSchema,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });

  const {
    signers,
    onEdit,
    onCreate,
    onDelete,
    onSubmit: onSubmitModal,
    canAddMoreSigners,
    minimumSigners,
    initialValues,
  } = useInvestmentSigners(investment?.id, investment.vault?.ta_account_type, {
    onSuccess: () => {
      modal.onClose();
    },
  });

  return (
    <WorkflowContainer
      title="Investment Details"
      onContinue={handleSubmit}
      buttonLabel={
        signers.length > 0 || investment.vault?.ta_account_type === "JOINT"
          ? "Continue"
          : "Skip without additional Signers"
      }
      buttonProps={{
        isDisabled: !isValid || !minimumSigners,
        isLoading: spinner,
      }}>
      <WorkflowProtection vaultId={investment.vault_id} />
      <WorkflowForm onSubmit={handleSubmit}>
        <Box mt={5}>
          <Password
            label={
              investment?.vault?.entity_type === "INDIVIDUAL"
                ? "SSN/TIN"
                : "Primary Signer's SSN/TIN"
            }
            isRequired
            maxLength={9}
            error={touched.tax_id && errors.tax_id}
            isInvalid={errors.tax_id && touched.tax_id}
            checkboxHintText="Show SSN/TIN"
            placeholder="Please enter your SSN/TIN without '-'"
            {...getFieldProps("tax_id")}
          />
        </Box>

        <DateField
          label={
            investment?.vault?.entity_type === "INDIVIDUAL"
              ? "Date of Birth"
              : "Primary Signer Date of Birth"
          }
          name="birthdate"
          dateFormat="yyyy-MM-dd"
          value={values.birthdate}
          onChange={(date) => {
            setFieldValue("birthdate", date);
          }}
          onBlur={() => {
            setTouched({ ...touched, birthdate: true });
          }}
          error={
            errors.birthdate && touched.birthdate ? errors.birthdate : undefined
          }
          isInvalid={errors.birthdate && touched.birthdate}
          showTimeSelect={false}
          showMonthDropdown
          showYearDropdown
          maxDate={(() => moment().subtract(18, "years").toDate())()}
          isRequired
        />

        {investment.vault?.ta_account_type === "TRUST" && (
          <Heading textStyle="h1" m={0} mb={0} mt={10} fontWeight="700">
            Trust Details
          </Heading>
        )}
        {investment.vault?.ta_account_type === "ENTITY" && (
          <Heading textStyle="h1" m={0} mb={0} mt={10} fontWeight="700">
            Entity Details
          </Heading>
        )}
        <Flex mb={5} />
        {investment?.vault?.ta_account_type === "ENTITY" && (
          <Flex flexDir="column" mb={5}>
            <Text>
              Select Entity Type <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              value={
                ENTITY_SUB_TYPE_OPTIONS.find(
                  (option) => option.value === values.ta_sub_type,
                )?.value
              }
              onChange={(value) => setFieldValue("ta_sub_type", value)}
              options={ENTITY_SUB_TYPE_OPTIONS}
              components={{
                IndicatorSeparator: null,
              }}
            />
          </Flex>
        )}

        {investment?.vault?.ta_account_type === "TRUST" && (
          <Flex flexDir="column" mb={5}>
            <Text>
              Select Trust Type <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              value={
                TRUST_SUB_TYPE_OPTIONS.find(
                  (option) => option.value === values.ta_sub_type,
                )?.value
              }
              onChange={(value) => setFieldValue("ta_sub_type", value)}
              options={TRUST_SUB_TYPE_OPTIONS}
              components={{
                IndicatorSeparator: null,
              }}
            />
          </Flex>
        )}

        {(investment?.vault?.ta_account_type === "ENTITY" ||
          investment?.vault?.ta_account_type === "TRUST") && (
          <>
            <Input
              label={`${capitalizeEveryWord(
                investment?.vault?.ta_account_type,
              )} Name`}
              placeholder={
                investment?.vault?.ta_account_type === "ENTITY"
                  ? "e.g. Acme Corporation"
                  : "John Doe and Jane Doe Revocable Trust dated 10/11/2020"
              }
              isRequired
              error={touched.company && errors.company}
              isInvalid={errors.company && touched.company}
              {...getFieldProps("company")}
            />
            <Checkbox
              name="copySSN"
              onChange={(e) => {
                setFieldValue("copySSN", e.target.checked);
                setDisableEIN(e.target.checked);
                if (e.target.checked) {
                  setFieldValue("ein", values.tax_id);
                }
              }}
              isChecked={values.copySSN}>
              {`My SSN is my ${capitalizeEveryWord(
                investment?.vault?.ta_account_type,
              )}'s Tax ID number`}
            </Checkbox>
          </>
        )}

        {investment?.vault?.ta_account_type === "JOINT" && (
          <Flex flexDir="column" mb={10}>
            <Text>
              Select Joint Type <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              value={
                JOINT_SUB_TYPE_OPTIONS.find(
                  (option) => option.value === values.ta_sub_type,
                )?.value
              }
              onChange={(value) => setFieldValue("ta_sub_type", value)}
              options={JOINT_SUB_TYPE_OPTIONS}
              components={{
                IndicatorSeparator: null,
              }}
            />
          </Flex>
        )}

        {(investment?.vault?.ta_account_type === "ENTITY" ||
          investment?.vault?.ta_account_type === "TRUST") && (
          <Box mt={5}>
            <Password
              label={
                investment.vault?.ta_account_type === "ENTITY"
                  ? "EIN"
                  : "Tax ID Number (TIN)"
              }
              placeholder="Please enter your EIN without dashes"
              isRequired
              isDisabled={disableEIN}
              maxLength={9}
              error={touched.ein && errors.ein}
              isInvalid={errors.ein && touched.ein}
              checkboxHintText={
                investment.vault?.ta_account_type === "ENTITY"
                  ? "Show EIN"
                  : "Show TIN"
              }
              {...getFieldProps("ein")}
            />
          </Box>
        )}
      </WorkflowForm>
      <Heading textStyle="h1" m={0} mb={0} mt={10} fontWeight="700">
        Signer Details
      </Heading>
      <Text mb={3}>
        {investment.vault?.ta_account_type === "JOINT"
          ? "You must add your joint signer before proceeding"
          : null}
      </Text>
      {investment.vault?.entity_type === "ENTITY" && (
        <Text>You can add additional signers to the investment</Text>
      )}
      <CardList my="4">
        <CardListItem>
          <Heading my="2">{userFullName(investment?.vault)}</Heading>
          <Text textStyle="context" m="0">
            {user?.email}
          </Text>
          <Text textStyle="context" mb="2">
            {user?.phone_number}
          </Text>
        </CardListItem>
        {signers.map((value, i) => (
          <CardListItem
            key={value.id || i}
            action={
              <IconButton
                onClick={() => onDelete(value.id)}
                icon={<Icon.Trash size="1.5em" color={equidefiRed} />}
              />
            }
            secondaryAction={
              <IconButton
                onClick={() => {
                  onEdit(value);
                  modal.onOpen();
                }}
                icon={<Icon.Edit size="1.5em" color={equidefiBlue} />}
              />
            }>
            <Heading my="2">{userFullName(value)}</Heading>
            <Text textStyle="context" m="0">
              {value.email}
            </Text>
            <Text textStyle="context" mb="2">
              {value.phone_number}
            </Text>
          </CardListItem>
        ))}
        {canAddMoreSigners && (
          <CardListItem
            key="add"
            onClick={() => {
              onCreate();
              modal.onOpen();
            }}
            action={<IconButton icon={<Icon.Plus size="1.5em" />} />}>
            <Text textStyle="context" my="2">
              Add another signer
            </Text>
          </CardListItem>
        )}
      </CardList>

      <SignerModal
        isOpen={modal.isOpen}
        initialValues={initialValues}
        onHide={modal.onClose}
        onSubmit={onSubmitModal}
      />
    </WorkflowContainer>
  );
};
